import React, { useState } from 'react';
import { Upload, File, User, MessageSquare, Camera, Check, X, Loader2 } from 'lucide-react';
import { message } from 'antd';
import axios from '../../services/axios'; // Assuming same axios setup as Code 1

const ModernDocumentUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    comment: '',
  });
  const [dragActive, setDragActive] = useState(false);
  const user = localStorage.getItem('user');
  const projectName = JSON.parse(localStorage.getItem('Project')); // Get projectName from localStorage

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList).map((file) => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: Math.random().toString(36).substr(2, 9),
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (id) => {
    setFiles(files.filter((file) => file.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      message.error('Please select files to upload');
      return;
    }
    if (!formData.comment.trim()) {
      message.error('Please add a comment');
      return;
    }

    setUploading(true);
    const formDataToSend = new FormData();
    files.forEach((fileObj) => {
      formDataToSend.append('file', fileObj.file); // Support multiple files
    });

    const payload = {
      projectName: projectName,
      user: user,
      comment: formData.comment,
    };

    formDataToSend.append('data', JSON.stringify(payload));

    try {
      const res = await axios.post('/upload-photo', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success('Photo uploaded successfully');
      setFiles([]);
      setFormData((prev) => ({ ...prev, comment: '' }));
    } catch (err) {
      if (err.response?.data?.msg) {
        message.error(err.response.data.msg);
      } else if (err.response?.data?.userName) {
        message.error(err.response.data.userName[0]);
      } else if (err.response?.status === 401) {
        message.error('User not authorized');
      } else {
        message.error('Something went wrong');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Upload Area */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-8">
                {/* Drag & Drop Zone */}
                <div
                  className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 ${
                    dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleFiles(e.target.files)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      <div
                        className={`w-20 h-20 rounded-full flex items-center justify-center transition-colors ${
                          dragActive ? 'bg-blue-100' : 'bg-gray-100'
                        }`}
                      >
                        <Camera className={`w-10 h-10 ${dragActive ? 'text-blue-600' : 'text-gray-400'}`} />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {dragActive ? 'Drop files here' : 'Upload your documents'}
                      </h3>
                      <p className="text-gray-500 mb-4">Drag and drop files here, or click to browse</p>
                      <p className="text-sm text-gray-400">Supports JPG, PNG, PDF, DOC, DOCX file</p>
                    </div>
                  </div>
                </div>

                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-8 space-y-3">
                    <h4 className="font-semibold text-gray-900 mb-4">Selected Files ({files.length})</h4>
                    {files.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <File className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 truncate max-w-xs">{file.name}</p>
                            <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFile(file.id)}
                          className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center transition-colors"
                        >
                          <X className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Sidebar */}
          <div className="space-y-6">
            {/* Project Info */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <File className="w-4 h-4 text-blue-600" />
                </div>
                Upload Photo/Video
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                  <input
                    type="text"
                    value={projectName}
                    disabled
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Uploaded By</label>
                  <div className="flex items-center space-x-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-gray-600 font-medium">{user}</span>
                  </div>
                </div>
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <MessageSquare className="w-4 h-4 text-green-600" />
                    </div>
                    Add Comment
                  </h3>
                  <textarea
                    value={formData.comment}
                    onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value }))}
                    placeholder="Describe what these documents show about the project..."
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Comment Section */}
            {/* <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <MessageSquare className="w-4 h-4 text-green-600" />
                </div>
                Add Comment
              </h3>
              <textarea
                value={formData.comment}
                onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value }))}
                placeholder="Describe what these documents show about the project..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div> */}

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
              className={`w-full py-4 px-6 rounded-2xl font-semibold text-white transition-all duration-300 ${
                uploading || files.length === 0
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
              }`}
            >
              {uploading ? (
                <div className="flex items-center justify-center space-x-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Uploading...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <Upload className="w-5 h-5" />
                  <span>Upload Documents</span>
                </div>
              )}
            </button>

            {uploading && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  <span className="text-blue-700 font-medium">Processing your upload...</span>
                </div>
                <div className="mt-3 bg-blue-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernDocumentUpload;